import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import logo from '@/assets/logo-small.webp';

interface TermsAcceptanceModalProps {
  userType: 'consumer' | 'business';
  onAccepted: () => void;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({ userType, onAccepted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateProfile } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allAccepted = termsAccepted && privacyAccepted;

  const handleAccept = async () => {
    if (!allAccepted) return;
    setIsSubmitting(true);
    await updateProfile({ terms_accepted_at: new Date().toISOString() } as any);
    setIsSubmitting(false);
    onAccepted();
  };

  const termsUrl = userType === 'consumer' ? '/legal/consumer-terms' : '/legal/business-terms';
  const privacyUrl = userType === 'consumer' ? '/legal/consumer-privacy' : '/legal/business-privacy';

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-lg mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full"
        >
          <div className="flex justify-center mb-8">
            <img src={logo} alt="Reserve" width={64} height={64} className="h-16 w-auto" />
          </div>

          <h1 className="text-xl font-display font-bold text-center mb-2">
            {t('accept_terms_title')}
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            {t('accept_terms_desc')}
          </p>

          <div className="space-y-4 mb-8">
            {/* Terms of Service */}
            <label className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card cursor-pointer">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">
                  {t('accept_terms_checkbox')}
                </span>
                <a
                  href={termsUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(termsUrl);
                  }}
                  className="flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
                >
                  {t('read_terms')}
                </a>
              </div>
            </label>

            {/* Privacy Policy */}
            <label className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card cursor-pointer">
              <Checkbox
                checked={privacyAccepted}
                onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">
                  {t('accept_privacy_checkbox')}
                </span>
                <a
                  href={privacyUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(privacyUrl);
                  }}
                  className="flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
                >
                  {t('read_privacy')}
                </a>
              </div>
            </label>

          </div>

          <Button
            onClick={handleAccept}
            disabled={!allAccepted || isSubmitting}
            className="w-full h-12 rounded-xl shadow-button"
          >
            {isSubmitting ? t('loading') : t('accept_and_continue')}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
